import { Icon } from 'client/widget-components/basicComponents/Icon/Icon';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { localizationService } from '@shared-services/localization-service';
import { cancelBooking } from '../api';
import { Booking } from '../types';

const getString = (key: string) => {
    return localizationService.str(
        `ui.runtimessr.calbooking.confirmation.success.${key}`
    );
};
interface BookingConfirmationProps {
    confirmationData: Booking;
    clearConfirmationData: () => void;
    rescheduleBooking: () => void;
}

function BookingConfirmation({
    confirmationData,
    clearConfirmationData,
    rescheduleBooking,
}: BookingConfirmationProps) {
    const [isLoading, setIsLoading] = React.useState(false);
    const bookingUid = confirmationData?.uid;
    const startDate = new Date(confirmationData.startTime);
    const endDate = new Date(confirmationData.endTime);

    const datePart = startDate.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };
    const startTime = startDate.toLocaleTimeString(undefined, timeOptions);
    const endTime = endDate.toLocaleTimeString(undefined, timeOptions);

    const timeZoneName = startDate
        .toLocaleTimeString(undefined, {
            timeZoneName: 'long',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .split(' ')
        .slice(2)
        .join(' ');

    const cancel = useCallback(() => {
        if (bookingUid) {
            setIsLoading(true);
            cancelBooking(bookingUid)
                .then(clearConfirmationData)
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [bookingUid]);

    return (
        <Wrapper data-auto='booking-confirmation-screen'>
            <Modal>
                <CloseSVG
                    width='22'
                    height='22'
                    viewBox='0 0 22 22'
                    fill='none'
                    onClick={clearConfirmationData}
                >
                    <line
                        x1='6'
                        y1='6'
                        x2='16'
                        y2='16'
                        stroke='currentColor'
                        strokeWidth='1'
                        strokeLinecap='round'
                    />
                    <line
                        x1='6'
                        y1='16'
                        x2='16'
                        y2='6'
                        stroke='currentColor'
                        strokeWidth='1'
                        strokeLinecap='round'
                    />
                </CloseSVG>
                <Header>
                    <SuccessIcon>
                        <svg viewBox='0 0 24 24'>
                            <polyline points='4 12 10 18 20 6' />
                        </svg>
                    </SuccessIcon>
                    <Title>{getString('title')}</Title>
                    <span>{getString('description')}</span>
                </Header>
                <hr />
                <Details>
                    <Detail>
                        <Label>{getString('what')}:</Label>
                        <Value>{confirmationData.title}</Value>
                    </Detail>
                    <Detail>
                        <Label>{getString('when')}:</Label>
                        <MultipleRowsValue>
                            <span>{datePart}</span>
                            <span>{`${startTime} - ${endTime} ${timeZoneName}`}</span>
                        </MultipleRowsValue>
                    </Detail>
                    <Detail>
                        <Label>{getString('who')}:</Label>
                        <Attendees>
                            <div>
                                <Value>
                                    {confirmationData.user.name}
                                    <HostTag>{getString('host')}</HostTag>
                                </Value>
                                <Value>
                                    {confirmationData.userPrimaryEmail}
                                </Value>
                            </div>
                            {confirmationData.attendees.map((attendee) => (
                                <div key={attendee.email}>
                                    <Value>{attendee.name}</Value>
                                    <Value>{attendee.email}</Value>
                                </div>
                            ))}
                        </Attendees>
                    </Detail>
                    <Detail>
                        <Label>{getString('where')}:</Label>
                        {confirmationData.videoCallUrl ? (
                            <Value>
                                <a
                                    href={confirmationData.videoCallUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {getString('videoCallLink')}
                                    <Icon
                                        name='icon-external-link'
                                        styles={{}}
                                    />
                                </a>
                            </Value>
                        ) : (
                            <Value>{confirmationData.location}</Value>
                        )}
                    </Detail>
                </Details>
                <hr />
                <Note>
                    <label>{getString('change')}</label>
                    <span onClick={rescheduleBooking}>
                        {getString('reschedule')}
                    </span>
                    <label>or</label>
                    <span onClick={cancel} data-disabled={isLoading}>
                        {getString('cancel')}
                    </span>
                </Note>
            </Modal>
        </Wrapper>
    );
}

const calcomColors = {
    bgSuccess: '#e8fbee',
    textSuccess: '#60ac68',
    hostTagBg: '#dfe7fb',
    hostTagText: '#2d3f81',
};

const componentWidth = 520;
const Wrapper = styled.div`
    position: fixed;
    z-index: 999;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Modal = styled.div`
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    width: ${componentWidth}px;
    padding: 32px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    color: #555;
    font-weight: 500;
    font-size: 13px;
    box-sizing: border-box;
    position: relative;
    cursor: default;
    hr {
        height: 1px;
        background: #ccc;
        width: 100%;
        margin-block: 32px;
    }
`;

const CloseSVG = styled.svg`
    position: absolute;
    right: 16px;
    top: 16px;
    cursor: pointer;
`;

const Header = styled.div`
    text-align: center;
    display: flex;
    flex-direction: column;
`;

const Title = styled.span`
    font-size: 24px;
    margin-block-end: 8px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Detail = styled.div`
    display: grid;
    grid-template-columns: 50px 1fr;
    a {
        display: flex;
        align-items: center;
        gap: 4px;
        text-decoration: none;
    }
`;

const Label = styled.span`
    font-weight: 600;
    text-align: start;
`;

const Attendees = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Value = styled.span`
    text-align: start;
    word-break: break-word;
`;

const MultipleRowsValue = styled.span`
    text-align: start;
    display: flex;
    flex-direction: column;
`;

const HostTag = styled.span`
    background-color: ${calcomColors.hostTagBg};
    color: ${calcomColors.hostTagText};
    padding: 2px 6px;
    box-sizing: border-box;
    border-radius: 4px;
    margin-inline-start: 8px;
`;

const Note = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    span {
        text-decoration: underline;
        cursor: pointer;
        &[data-disabled='true'] {
            pointer-events: none;
            opacity: 0.5;
        }
    }
`;

const SuccessIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background-color: ${calcomColors.bgSuccess};
    margin: 0 auto 16px auto;

    svg {
        width: 32px;
        height: 32px;
        stroke: ${calcomColors.textSuccess};
        stroke-width: 2;
        fill: none;
    }
`;
export default BookingConfirmation;
